import {Roles} from '@hconnect/apiclient'
import {
  AreaNode,
  DepartmentNode,
  UpmEntityType,
  TreeNode,
  EquipmentNode
} from '@hconnect/common/types'
import {useUserContext} from '@hconnect/login'
import {dataTestId} from '@hconnect/uikit'
import {icon, Pill} from '@hm/fundamental'
import {
  ChevronRight,
  ExpandMore,
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  Create as CreateIcon,
  WarningAmberOutlined as WarningAmberOutlinedIcon,
  ErrorOutline as ErrorOutlineIcon
} from '@mui/icons-material'
import {
  Box,
  Divider,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  alpha
} from '@mui/material'
import {styled} from '@mui/material/styles'
import {TFunction} from 'i18next'
import React, {useCallback} from 'react'
import {NodeApi, NodeRendererProps} from 'react-arborist'
import {useTranslation} from 'react-i18next'

import {useSettingsQuery} from '../../common/providers/ApiInterface'
import {UNASSIGNED_DEPARTMENT_CODE} from '../consts'
import {ACTIVE_STATUS_ID} from '../hooks/upmEntityConfig/useUpdateDepartmentStatus'
import {useItemDetails} from '../providers'
import {useItemLink} from '../providers/ItemLinkProvider'

import {PlantStructureActionButton} from './PlantStructureActionButton'

export interface PlantStructureNodeExtraProps {
  allowManualUpmCreation?: boolean
}

type PlantStructureNodeProps<T extends TreeNode> = NodeRendererProps<T> &
  PlantStructureNodeExtraProps

const StyledListItem = styled(ListItem)(() => ({
  '&:hover .icon-button': {
    visibility: 'visible'
  },
  '.icon-button': {
    visibility: 'hidden'
  }
}))

const getLabel = (node: TreeNode) => node.text ?? ''

const statusTranslationMap: Record<string, string> = {
  Active: 'plantSetup.departmentStatusActive',
  Mothballed: 'plantSetup.departmentStatusMothballed',
  Inactive: 'plantSetup.departmentStatusInactive',
  Decommissioned: 'plantSetup.departmentStatusDecommissioned'
}

const statusIconMap: Record<string, string> = {
  Inactive: icon.mdiPause,
  Mothballed: icon.mdiFileCabinet,
  Decommissioned: icon.mdiCancel
}

const DepartmentStatusPill: React.FC<{upmId: string}> = React.memo(({upmId}) => {
  const {t} = useTranslation()
  const {data: departmentDetails} = useSettingsQuery('upmDepartmentDetails', [upmId], {
    enabled: true,
    staleTime: 5 * 60 * 1000
  })

  if (!departmentDetails) return null
  if (!departmentDetails.statusId || departmentDetails.statusId === ACTIVE_STATUS_ID) return null

  const translationKey = departmentDetails.statusText
    ? statusTranslationMap[departmentDetails.statusText]
    : undefined
  const statusLabel = translationKey ? t(translationKey) : departmentDetails.statusText

  if (!statusLabel) return null

  const labelIcon = departmentDetails.statusText
    ? statusIconMap[departmentDetails.statusText]
    : undefined

  return (
    <Pill
      type="neutral"
      variant="shade"
      size="regular"
      text={statusLabel}
      labelIcon={labelIcon}
      data-cy={`department-status-${upmId}`}
    />
  )
})

DepartmentStatusPill.displayName = 'DepartmentStatusPill'

const getShouldHideArrow = <T extends TreeNode>(node: NodeApi<T>) => {
  return (
    node.isLeaf || node.data.children.length === 0 || node.data.itemType === UpmEntityType.Plant
  )
}

const getRoleFlags = (
  userRoles: string[] | undefined,
  node: TreeNode,
  allowManualUpmCreation: boolean
) => {
  const hasBoardMemberRole = !!userRoles && userRoles.includes('BOARD_MEMBER')
  const canUpdateDepartments = !!userRoles && userRoles.includes(Roles.HproduceCCCSupporter)

  return {
    hasBoardMemberRole,
    canUpdateDepartments,
    canUpdateAreaDepartments: canUpdateDepartments && node.itemType === UpmEntityType.Area,
    canAddAreaDepartments:
      canUpdateDepartments && node.itemType === UpmEntityType.Plant && allowManualUpmCreation,
    canDeleteAreaDepartments: canUpdateDepartments && node.itemType === UpmEntityType.Department,
    canDeleteEquipments: canUpdateDepartments && node.itemType === UpmEntityType.Equipment
  }
}

const renderStatusLabel = (text: string, testId: string, icon?: React.ReactNode) => (
  <Box display="flex" flexDirection="row" alignItems="center" gap={0.5} {...dataTestId(testId)}>
    {icon}
    <span>{text}</span>
  </Box>
)

const getSecondaryLabel = (node: TreeNode, t: TFunction) => {
  if (
    node.itemType === UpmEntityType.Department &&
    node?.type?.code === UNASSIGNED_DEPARTMENT_CODE
  ) {
    return renderStatusLabel(
      t('plantSetup.upmDepartmentNotMapped'),
      `not-mapped-${node.upmId}`,
      <WarningAmberOutlinedIcon fontSize="small" color="warning" />
    )
  }

  if (
    (node.itemType === UpmEntityType.Department || node.itemType === UpmEntityType.Equipment) &&
    node.isDeletedInSource
  ) {
    return renderStatusLabel(
      t('plantSetup.itemDeletedInSap'),
      `deleted-in-sap-${node.upmId}`,
      <ErrorOutlineIcon fontSize="small" color="error" />
    )
  }

  if (node.itemType === UpmEntityType.Department) {
    return renderStatusLabel(node.type?.name ?? '', `department-subtitle-${node.upmId}`)
  }

  return null
}

export const PlantStructureNode = <T extends TreeNode>({
  node,
  style,
  allowManualUpmCreation = false
}: PlantStructureNodeProps<T>) => {
  const {loggedUser} = useUserContext()
  const {setItemToLink} = useItemLink()
  const {setEditedArea, setAddArea, setDepartmentToDelete, setEquipmentToDelete} = useItemDetails()

  const userRoles = loggedUser?.hasRoles
  const {t} = useTranslation()

  const isDepartment = node.data.itemType === UpmEntityType.Department

  const {
    hasBoardMemberRole,
    canUpdateAreaDepartments,
    canAddAreaDepartments,
    canDeleteAreaDepartments,
    canDeleteEquipments
  } = getRoleFlags(userRoles, node.data, allowManualUpmCreation)

  const shouldHideArrow = getShouldHideArrow(node)

  const shouldShowAddSubdomain =
    allowManualUpmCreation &&
    hasBoardMemberRole &&
    (node.data.itemType === UpmEntityType.Department ||
      node.data.itemType === UpmEntityType.Equipment)

  const shouldShowAddArea = canAddAreaDepartments && node.data.itemType === UpmEntityType.Plant

  const handleExpandCollapseNode = useCallback(
    (event: React.MouseEvent) => {
      node.isInternal && node.toggle()
      event.stopPropagation()
    },
    [node]
  )

  const handleDeleteClick = useCallback(
    (event: React.MouseEvent) => {
      setDepartmentToDelete(node.data as DepartmentNode, node.parent?.data?.upmId)
      event.stopPropagation()
    },
    [node.data, node.parent?.data?.upmId, setDepartmentToDelete]
  )

  const handleDeleteEquipmentClick = useCallback(
    (event: React.MouseEvent) => {
      setEquipmentToDelete(node.data as EquipmentNode)
      event.stopPropagation()
    },
    [node.data, setEquipmentToDelete]
  )

  const handleAddClick = useCallback(
    (event: React.MouseEvent) => {
      if (canUpdateAreaDepartments) {
        setEditedArea(node.data as AreaNode)
      } else {
        setItemToLink(node.data.upmId)
      }
      event.stopPropagation()
    },
    [canUpdateAreaDepartments, node.data, setItemToLink, setEditedArea]
  )

  const handleAddAreaClick = useCallback(
    (event: React.MouseEvent) => {
      if (canAddAreaDepartments) {
        setAddArea(node.data as AreaNode)
      }
      event.stopPropagation()
    },
    [canAddAreaDepartments, node.data, setAddArea]
  )

  const label = getLabel(node.data)
  const secondaryLabel = getSecondaryLabel(node.data, t)

  return (
    <Stack>
      <StyledListItem dense disablePadding {...dataTestId(`list-item-${label}`)}>
        <ListItemButton
          style={style}
          sx={{
            backgroundColor: ({palette}) =>
              node.isSelected ? alpha(palette.primary.light, 0.1) : 'inherit',
            height: 56
          }}
        >
          <ListItemIcon
            sx={{
              display: shouldHideArrow ? 'none' : 'flex',
              minWidth: 30
            }}
            onClick={handleExpandCollapseNode}
          >
            {node.isOpen ? <ExpandMore color="primary" /> : <ChevronRight color="primary" />}
          </ListItemIcon>
          <ListItemText
            sx={{pl: shouldHideArrow ? 4 : 0}}
            primaryTypographyProps={{noWrap: true}}
            primary={label}
            secondary={secondaryLabel}
          />
          {isDepartment && <DepartmentStatusPill upmId={node.data.upmId} />}
          <PlantStructureActionButton
            shouldBeDisplayed={shouldShowAddArea}
            testId={`add-area-${node.data.upmId}`}
            icon={<CreateIcon />}
            handleEvent={handleAddAreaClick}
          />
          <PlantStructureActionButton
            shouldBeDisplayed={canUpdateAreaDepartments}
            testId={`add-area-departments-${node.data.upmId}`}
            icon={<CreateIcon />}
            handleEvent={handleAddClick}
          />
          <PlantStructureActionButton
            shouldBeDisplayed={shouldShowAddSubdomain}
            icon={<AddIcon />}
            handleEvent={handleAddClick}
          />
          <PlantStructureActionButton
            shouldBeDisplayed={canDeleteAreaDepartments}
            testId={`delete-area-departments-${node.data.upmId}`}
            icon={<DeleteIcon color="error" />}
            handleEvent={handleDeleteClick}
          />
          <PlantStructureActionButton
            shouldBeDisplayed={canDeleteEquipments}
            testId={`delete-equipments-${node.data.upmId}`}
            icon={<DeleteIcon color="error" />}
            handleEvent={handleDeleteEquipmentClick}
          />
        </ListItemButton>
      </StyledListItem>
      <Divider variant="middle" sx={{mx: 4}} />
    </Stack>
  )
}
